// src/pages/ForgotPassword.jsx
import React, { useState } from "react";
import LanguageLink from "../routing/LanguageLink";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import loginAnimation from "../../assets/animations/login-animation.json";
import SEOHead from "../section/SeoHead";
import HelpAccessingSection from "../section/HelpAccessingSection";
import { ArrowLeft, CheckCircle, Mail, Lock, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const ForgotPassword = () => {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState("");

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Forgot Password - Citizen Hub",
        description: "Securely reset your Citizen Hub account password.",
    };

    const handleSendCode = async (e) => {
        e.preventDefault();
        if (!email.includes("@")) return alert("Please enter a valid email address");

        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
            setMessage(`${t("auth.forgot_password.code_sent")} ${email}`);
            setStep(2);
        }, 2000);
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        if (code.length !== 6 || !/^\d+$/.test(code)) {
            return alert("Please enter a valid 6-digit code");
        }

        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
            setStep(3);
        }, 1500);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            return alert("Passwords do not match");
        }
        if (newPassword.length < 8) {
            return alert("Password must be at least 8 characters long");
        }

        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
            setMessage("Your password has been successfully reset!");
            setStep(4);
        }, 2000);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
            <SEOHead
                title="Forgot Password - Citizen Hub"
                description="Reset your password securely and regain access to your account."
                canonicalUrl="https://citiznhub.com/forgot-password"
                structuredData={structuredData}
            />

            <div className="py-10 px-4">
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                        <div className="order-2 lg:order-1">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-2xl shadow-2xl p-8 lg:p-10"
                            >
                                <div className="text-center mb-10">
                                    <h1 className="text-3xl font-bold text-gray-900">{t("auth.forgot_password.title")}</h1>
                                    <p className="text-gray-600 mt-2">{t("auth.forgot_password.subtitle")}</p>
                                </div>

                                <div className="flex justify-center mb-12">
                                    {[1, 2, 3].map((s) => (
                                        <div key={s} className="flex items-center">
                                            <div
                                                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white transition-all duration-300 ${step >= s
                                                    ? step === 4 && s === 3
                                                        ? "bg-green-600"
                                                        : "bg-blue-600"
                                                    : "bg-gray-300"
                                                    }`}
                                            >
                                                {step > s || (step === 4 && s === 3) ? (
                                                    <CheckCircle className="w-6 h-6" />
                                                ) : (
                                                    s
                                                )}
                                            </div>
                                            {s < 3 && (
                                                <div
                                                    className={`w-24 h-1 mx-3 transition-all duration-500 ${step > s ? "bg-blue-600" : "bg-gray-300"
                                                        }`}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <AnimatePresence mode="wait">
                                    {step === 1 && (
                                        <motion.form
                                            key="email"
                                            initial={{ opacity: 0, x: 50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -50 }}
                                            onSubmit={handleSendCode}
                                            className="space-y-6"
                                        >
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    <Mail className="inline w-4 h-4 mr-1" /> {t("auth.forgot_password.email_label")}
                                                </label>
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    required
                                                    placeholder="you@example.com"
                                                    className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                />
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-70 transition-all flex items-center justify-center gap-2"
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <Loader2 className="animate-spin w-5 h-5" />
                                                        Sending Code...
                                                    </>
                                                ) : (
                                                    t("auth.forgot_password.send_code_button")
                                                )}
                                            </button>
                                        </motion.form>
                                    )}

                                    {step === 2 && (
                                        <motion.form
                                            key="code"
                                            initial={{ opacity: 0, x: 50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -50 }}
                                            onSubmit={handleVerifyCode}
                                            className="space-y-6"
                                        >
                                            <div className="text-center p-5 bg-blue-50 rounded-xl border border-blue-100">
                                                <p className="text-sm text-blue-800">{t("auth.forgot_password.code_sent")}</p>
                                                <p className="font-bold text-blue-900 mt-1">{email}</p>
                                            </div>

                                            <input
                                                type="text"
                                                maxLength="6"
                                                value={code}
                                                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                                placeholder="000000"
                                                className="w-full text-center text-4xl font-bold tracking-widest letter-spacing-4 px-4 py-5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />

                                            <div className="flex gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setStep(1)}
                                                    className="flex-1 border border-gray-300 py-3 rounded-xl hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                                                >
                                                    <ArrowLeft className="w-4 h-4" />
                                                    Back
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={isLoading || code.length !== 6}
                                                    className="flex-1 bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 disabled:opacity-70 transition-all flex items-center justify-center gap-2"
                                                >
                                                    {isLoading ? (
                                                        <Loader2 className="animate-spin w-5 h-5" />
                                                    ) : (
                                                        t("auth.forgot_password.verify_code_button")
                                                    )}
                                                </button>
                                            </div>
                                        </motion.form>
                                    )}

                                    {step === 3 && (
                                        <motion.form
                                            key="password"
                                            initial={{ opacity: 0, x: 50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -50 }}
                                            onSubmit={handleResetPassword}
                                            className="space-y-6"
                                        >
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    <Lock className="inline w-4 h-4 mr-1" /> {t("auth.forgot_password.new_password_label")}
                                                </label>
                                                <input
                                                    type="password"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    required
                                                    minLength="8"
                                                    className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                                                    placeholder="••••••••"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {t("auth.forgot_password.confirm_password_label")}
                                                </label>
                                                <input
                                                    type="password"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    required
                                                    className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                                                    placeholder="••••••••"
                                                />
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className="w-full bg-green-600 text-white py-4 rounded-xl font-semibold hover:bg-green-700 disabled:opacity-70 transition-all flex items-center justify-center gap-2"
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <Loader2 className="animate-spin w-5 h-5" />
                                                        Resetting Password...
                                                    </>
                                                ) : (
                                                    t("auth.forgot_password.reset_button")
                                                )}
                                            </button>
                                        </motion.form>
                                    )}

                                    {step === 4 && (
                                        <motion.div
                                            key="success"
                                            initial={{ scale: 0.9, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            className="text-center py-12"
                                        >
                                            <CheckCircle className="w-24 h-24 text-green-600 mx-auto mb-6" />
                                            <h2 className="text-3xl font-bold text-gray-900 mb-3">
                                                {t("auth.forgot_password.success_title")}
                                            </h2>
                                            <p className="text-gray-600 text-lg mb-8">
                                                {t("auth.forgot_password.success_text")}
                                            </p>
                                            <LanguageLink
                                                to="/login"
                                                className="inline-block bg-blue-600 text-white px-10 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all"
                                            >
                                                {t("auth.forgot_password.back_to_signin")}
                                            </LanguageLink>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {message && !step === 4 && (
                                    <p className="mt-6 text-center text-green-600 font-medium bg-green-50 py-3 rounded-lg">
                                        {message}
                                    </p>
                                )}
                            </motion.div>
                        </div>

                        <div className="order-1 lg:order-2">
                            <div className="flex flex-col items-center justify-center text-center">
                                <Lottie
                                    animationData={loginAnimation}
                                    className="w-full max-w-md"
                                    loop={true}
                                    autoplay={true}
                                />
                                <div className="mt-8">
                                    <h3 className="text-3xl font-bold text-gray-800 mb-3">
                                        {t("auth.login.welcome_back")}
                                    </h3>
                                    <p className="text-gray-600 text-lg max-w-md mx-auto">
                                        {t("auth.login.welcome_text")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <HelpAccessingSection />
        </div>
    );
};

export default ForgotPassword;